import SelectDataSource from './select-data-source';

/**
 * Base class for combobox implementations.
 *
 * - Supports different data sources
 * - Mouse/touch event listeners for activating or selecting items
 * - No keyboard functionality, since expected functionality depends on implementation
 * - Triggers custom events on container or (native select element if SelectDataSource is used):
 *    - `combobox.active` When element is activated or deactivated by calling {@link setActiveItem}. Detail is activated item or null.
 *    - `combobox.destroy` After {@link destroy} is called and before references have been removed. No detail.
 *    - `combobox.init` Called when combobox has been fully initialized. No detail.
 *    - `combobox.render` Called when list rendering is completed. No detail.
 *    - `combobox.render.group` Called when item group is rendered. Detail is group element.
 *    - `combobox.render.item` Called when item is rendered. Detail is item element.
 *    - `combobox.toggle` When combobox list is opened or closed. Detail is boolean representing open state.
 *    - `combobox.update` When element is updated was updated by data source. Detail is array of updated items.
 *    - `combobox.value` After {@link setValue} is called and before data source value is updated. Detail is object that contains selected items, current value and old value.
 * - Combobox events can optionally be handled with function callbacks (function callback is run before event is triggered):
 *    - `onDestroy` functionally same as `combobox.destroy` event
 *    - `onInit` functionally same as `combobox.init` event
 *    - `onItemActivated` functionally same as `combobox.active` event
 *    - `onItemRender` functionally same as `combobox.render.(item|group)` event
 *    - `onItemSelected` functionally same as `combobox.value` event
 *    - `onRender` functionally same as `combobox.render` event
 *    - `onToggle` functionally same as `combobox.toggle` event
 *    - `onUpdate` functionally same as `combobox.update` event
 * - Renderer functions that can be used to customize generated html content:
 *    - `selectionRenderer` Overrides selected item preview shown in control element. Note! Allowed content depends on control element type.
 *
 * @abstract
 */
class Combobox {

  static instances = 0;

  /**
   * @protected
   */
  _id = '';

  /**
   * @protected
   */
  _onDocumentClick = null;

  /**
   * @protected
   */
  _onLabelClick = null;

  /**
   * @protected
   */
  _config = null;

  /**
   * @protected
   */
  _elements = null;

  /**
   * @protected
   */
  _dataSource = null;

  /**
   * Array of item values that matched previous filter.
   * @protected
   */
  _filteredItems = null;

  /**
   * Array of option items.
   * @protected
   */
  _items = [];

  /**
   * Create new combobox.
   *
   * @param {ComboboxOptions} options Combobox options.
   */
  constructor(options = {}) {
    const select = options.select;
    let target = null;
    if (select) {
      target = document.createElement('div');
      select.insertAdjacentElement('afterend', target);
    }

    this._dataSource = select ? new SelectDataSource(select) : options.dataSource;
    if (!this._dataSource) {
      throw new Error('Datasource not defined.');
    }

    const baseClass = options.cssClassPrefix || 'combobox';
    this._config = {
      cssContainerClass: baseClass,
      cssContainerOpenedClass: 'open',
      cssControlClass: baseClass + '__control',
      cssListClass: baseClass + '__list',
      cssOptionClass: baseClass + '__option',
      cssOptionActiveClass: 'active',
      cssOptionDisabledClass: 'disabled',
      cssOptionSelectedClass: 'selected',
      cssOptgroupClass: baseClass + '__group',
      cssOptgroupDisabledClass: 'disabled',
      cssOptgroupLabelClass: baseClass + '__group-label',
      cssNoResultsClass: baseClass + '__no-results',
      target: target,
      disabled: select ? select.disabled : false,
      hidden: select ? select.hidden : false,
      multiple: select ? select.multiple : false,
      maxSelections: null,
      minSelections: 0,
      allowEmpty: true,
      placeholder: '',
      noResults: 'No results found.',
      onDestroy: null,
      onInit: null,
      onItemActivated: null,
      onItemRender: null,
      onItemSelected: null,
      onRender: null,
      onToggle: null,
      onUpdate: null,
      selectionRenderer: null,
      ...(options || {})
    };

    if (!this._config.allowEmpty && !this._config.placeholder) {
      this._config.placeholder = Array.from(select.options).find(item => item.value === '')?.innerText || '';
    }

    Combobox.instances++;
    this._id = options.id || ('combobox-' + Combobox.instances);


    this.initializeElements();

    this.addExternalEventListeners();
    this.addEventListeners();

    this._config?.onInit?.call(this);
    this.dispatchEvent('init');
  }

  /**
   * Get id prefix.
   *
   * @returns {string}
   * @protected
   */
  getId() {
    return this._id;
  }

  /**
   * Get configurations.
   *
   * @returns {ComboboxOptions}
   * @protected
   */
  getConfig() {
    return this._config;
  }

  /**
   * Get combobox elements.
   *
   * @returns {ComboboxElements} Combobox elements, sub classes may extend this if needed.
   * @protected
   */
  getElements() {
    return this._elements;
  }

  /**
   * Get data source.
   *
   * @returns {SelectDataSource} Data source.
   */
  getDataSource() {
    return this._dataSource;
  }

  /**
   * Create combobox elements. Note! Base class only returns list and label elements.
   * Implementing class must provide control and container class and may override existing elements.
   *
   * @return {ComboboxElements} Created elements.
   * @abstract
   * @protected
   */
  createElements() {
    const id = this.getId();
    const config = this.getConfig();

    // Create list element
    const list = document.createElement('ul');
    list.id = id + '-list';
    list.classList.add(config.cssListClass);
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');
    list.setAttribute('data-disabled', config.disabled ? 'true' : 'false');
    list.setAttribute('aria-multiselectable', config.multiple ? 'true' : 'false');

    // Reassing native select label to combobox
    const select = this.getDataSource()?.getNativeSelect();
    const label = config.label || (select && select.id ? document.querySelector(`[for="${select.id}"]`) : null);
    if (label) {
      label.id = label.id || (this.getId() + '-label');
      label.htmlFor = '';
    }

    return {
      container: null,
      control: null,
      label,
      list
    };
  }

  /**
   * Initializes elements.
   *
   * @protected
   */
  initializeElements() {
    const config = this.getConfig();
    const elements = this.createElements();

    // Validate that subclass rendered needed elements
    if (!elements.control) {
      throw new Error('Control element not defined.');
    }
    if (!elements.container) {
      throw new Error('Container element not defined.');
    }
    if (!config.target) {
      throw new Error('Target element missing.');
    }

    this._elements = elements;

    this.renderContent();

    config.target.replaceWith(elements.container);
    const select = this.getDataSource()?.getNativeSelect();
    if (select) {
      select.style.display = 'none';
      select.setAttribute('data-combobox', this.getId());
    }
  }

  /**
   * Trigger internal event.
   *
   * @param {string} name Event name.
   * @param {mixed} data Event data.
   * @protected
   */
  dispatchEvent(name, data = null) {
    const eventTarget = this.getDataSource()?.getNativeSelect() || this.getElements().container;
    eventTarget.dispatchEvent(new CustomEvent('combobox.' + name, { detail: data }));
  }

  /**
   * Replaces control content with selected items.
   *
   * @param {object} data Old value, current value and array of selected items.
   * @protected
   */
  renderSelection(data) {
    if (!data) {
      data = {
        value: this.getValue(),
        oldValue: [],
        selectedItems: this.getSelectedItems()
      };
    }
    const config = this.getConfig();
    let content = config?.selectionRenderer?.call(this, data.selectedItems, data.value, data.oldValue);

    if (typeof content !== 'string') {
      // Deduplicate by value - take only first match for each unique value
      const seenValues = new Set();
      const uniqueItems = data.selectedItems.filter(item => {
        if (seenValues.has(item.dataset.value)) {
          return false;
        }
        seenValues.add(item.dataset.value);
        return true;
      });
      content = uniqueItems.map(item => item.dataset.text).join(', ');
    }

    this.updateSelection(content, data);
  }

  /**
   * Updates selection preview shown in control.
   *
   * @param {string} content Content to set in control.
   * @param {object} data Data that was used to render preview content.
   * @protected
   * @abstract
   */
  updateSelection(content, data) {
    // Nothing
  }

  /**
   * Add event listeners which are attached to elements outside of internal scope and should be removed when element is destroyed.
   *
   * @protected
   */
  addExternalEventListeners() {
    const elements = this.getElements();

    // Close when user clicks outside of element
    const onClick = (e) => {
      if (elements.container.contains(e.target) || elements?.label?.contains(e.target)) {
        return;
      }

      this.close(false);
    };
    this._onDocumentClick = onClick.bind(this);
    document.addEventListener('click', this._onDocumentClick);

    // Focus control when label is clicked
    if (elements.label) {
      const onLabelClick = (e) => {
        const button = elements.control;

        // Prevent focusing if the control is disabled
        if (button.disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        this.open(true);
      };
      this._onLabelClick = onLabelClick.bind(this);
      elements.label.addEventListener('click', this._onLabelClick);
    }
  }

  /**
   * Attach listeners to internal elements.
   *
   * @protected
   */
  addEventListeners() {
    const dataSource = this.getDataSource();
    dataSource.on('change', (value) => {
      this._updateValue(value);
    });
    dataSource.on('content', (value) => {
      this.renderContent();
    });
    dataSource.on('update', (updates) => {
      const elements = this.getElements();
      const config = this.getConfig();

      updates.forEach(update => {
        if (update.type === 'select') {
          if (update.attribute === 'hidden') {
            elements.container.hidden = update.value;
          }
          else if (update.attribute === 'disabled') {
            elements.list.setAttribute('data-disabled', update.value ? 'true' : 'false');
            elements.control.disabled = update.value;
          }
        }
        else if (update.type === 'group') {
          const optgroup = this.getElements().list.querySelector(`[role="group"][data-label="${update.updatedItem.label}"]`);
          if (!optgroup) {
            return;
          }

          if (update.attribute === 'disabled') {
            optgroup.setAttribute('aria-disabled', update.value ? 'true' : 'false');
            optgroup.classList.toggle(config.cssOptgroupDisabledClass, update.value);
            document.getElementById(`${optgroup.id}-container`).classList.toggle(config.cssOptgroupDisabledClass, update.value);
            document.getElementById(`${optgroup.id}-label`).classList.toggle(config.cssOptgroupDisabledClass, update.value);
          }
          else if (update.attribute === 'hidden') {
            optgroup.hidden = update.value;
          }
        }
        else if (update.type === 'option') {
          const option = this.getItems().find(item => item.dataset.value === update.updatedItem.value);
          if (!option) {
            return;
          }

          if (update.attribute === 'disabled') {
            option.setAttribute('aria-disabled', update.value ? 'true' : 'false');
            option.classList.toggle(config.cssOptionDisabledClass, update.value);
          }
          else if (update.attribute === 'hidden') {
            option.hidden = update.value;
          }
        }
      });

      this.getConfig()?.onUpdate?.call(this, updates);
      this.dispatchEvent('update');
    });

    const elements = this.getElements();

    // Update native select value when option is selected
    elements.list.addEventListener('click', (e) => {
      const option = e.target.closest('[role="option"]');
      if (!option || !this.isItemSelectable(option)) {
        return;
      }

      const values = this.getValue();
      const isAddition = !values.includes(option.dataset.value);
      const max = this.getConfig().maxSelections;
      const min = this.getConfig().minSelections;
      if (isAddition && values.length === max) {
        return;
      }
      else if (!isAddition && min === values.length) {
        return;
      }

      this.toggleValue(option.dataset.value);

      if (!this.isMultiSelect()) {
        this.close(true);
      }
      else {
        this.getElements().control.focus();
      }
    });

    // Set active item when hovered
    elements.list.addEventListener('mousemove', (e) => {
      const option = e.target.closest('[role="option"]');
      if (option && this.isItemSelectable(option)) {
        this.setActiveItem(option, false);
      }
    });

    elements.control.addEventListener('keyup', this.onKeyUp.bind(this));
    elements.control.addEventListener('keydown', this.onKeyDown.bind(this));
    elements.control.addEventListener('click', this.onControlClick.bind(this));
    elements.control.addEventListener('focus', this.onControlFocus.bind(this));
    elements.control.addEventListener('blur', this.onControlBlur.bind(this));
  }

  /**
   * Called when control input receives click event. Toggles combobox open state.
   *
   * @param {MouseEvent} e Original event.
   * @protected
   */
  onControlClick(e) {
    if (this.isOpen()) {
      this.close(true);
    }
    else {
      this.open(true);
    }
  }

  /**
   * Called when control input is focused. No default implementation.
   *
   * @param {FocusEvent} e Original event.
   * @protected
   */
  onControlFocus(e) {
    // Nothing
  }

  /**
   * Called when control input is blured. Closes combobox if target element is not inside combobox container.
   *
   * @param {FocusEvent} e Original event.
   * @protected
   */
  onControlBlur(e) {
    if (!this.getElements().container.contains(e.relatedTarget)) {
      this.close(false);
    }
  }

  /**
   * Called when control input receives keydown event. No default implementation.
   *
   * @param {KeyboardEvent} e Original event.
   * @protected
   */
  onKeyDown(e) {
    // Nothing
  }

  /**
   * Called when control input receives keyup event. No default implementation.
   *
   * @param {KeyboardEvent} e Original event.
   * @protected
   */
  onKeyUp(e) {
    // Nothing
  }

  /**
   * Create new option element.
   *
   * @param {object} data Element data.
   * @returns {HTMLElement} Created element.
   * @protected
   */
  createOptionElement(data) {
    const config = this.getConfig();

    const item = document.createElement('li');
    item.id = data.id;
    item.hidden = data.hidden;
    item.classList.add(config.cssOptionClass);
    item.classList.toggle(config.cssOptionDisabledClass, data.disabled);
    item.classList.toggle(config.cssOptionSelectedClass, data.selected);
    item.setAttribute('role', 'option');
    item.setAttribute('data-active', 'false');
    item.setAttribute('data-value', data.value);
    item.setAttribute('data-text', data.text);
    item.setAttribute('aria-disabled', data.disabled ? 'true' : 'false');
    item.setAttribute('aria-selected', data.selected ? 'true' : 'false');
    item.innerHTML = data.text;

    return item;
  }

  /**
   * Create new group element.
   *
   * @param {object} data Element data.
   * @returns {HTMLElement} Created element.
   * @protected
   */
  createOptGroupElement(data) {
    const config = this.getConfig();

    const group = document.createElement('li');
    group.id = data.id;
    group.hidden = data.hidden;
    group.classList.add(config.cssOptgroupClass);
    group.classList.toggle(config.cssOptgroupDisabledClass, data.disabled);
    group.setAttribute('aria-labelledby', group.id + '-label');
    group.setAttribute('aria-disabled', data.disabled ? 'true' : 'false');
    group.setAttribute('role', 'group');
    group.setAttribute('data-label', data.label);

    // Add group label as node before group
    const groupLabel = document.createElement('span');
    groupLabel.id = group.id + '-label';
    groupLabel.innerHTML = data.label;
    groupLabel.classList.add(config.cssOptgroupLabelClass);
    groupLabel.classList.toggle(config.cssOptgroupDisabledClass, data.disabled);

    const groupList = document.createElement('ul');

    group.appendChild(groupLabel);
    group.appendChild(groupList);

    return group;
  }

  /**
   * Renders list content from internal object state.
   */
  renderContent() {
    const elements = this.getElements();
    const config = this.getConfig();
    const matches = this.getFilteredItems();
    const content = [];
    const items = [];

    const renderItem = (item) => {
      if (!config.allowEmpty && item.value === '') {
        return;
      }
      if (matches && !matches.includes(item.value)) {
        return;
      }

      const node = this.createOptionElement(item);
      config?.onItemRender?.call(this, node);
      this.dispatchEvent('render.item', node);
      items.push(node);
      return node;
    };

    // Include items that match active filter
    this.getDataSource().getContent().forEach(item => {
      if (Array.isArray(item.items)) {
        const groupItems = [];

        item.items.forEach(child => {
          const node = renderItem(child);
          if (node) {
            groupItems.push(node);
          }
        });

        if (groupItems.length > 0) {
          const group = this.createOptGroupElement(item);
          config?.onItemRender?.call(this, group);
          this.dispatchEvent('render.group', group);
          group.querySelector('ul').append(...groupItems);
          content.push(group);
        }
      }
      else {
        const node = renderItem(item);
        if (node) {
          content.push(node);
        }
      }
    });

    // Append no results element
    if (content.length === 0 && config.noResults) {
      const empty = document.createElement('li');
      empty.classList.add(config.cssNoResultsClass);
      empty.setAttribute('role', 'presentation');
      empty.innerHTML = config.noResults;
      content.push(empty);
    }

    elements.list.innerHTML = '';
    elements.list.append(...content);
    this._items = items;

    this.renderSelection();

    if (this.isOpen()) {
      const item = this.findSelectableItem(null, 0);
      if (item) {
        this.setActiveItem(item);
      }
    }

    this.getConfig()?.onRender?.call(this);
    this.dispatchEvent('render');
  }

  /**
   * Get all options.
   *
   * @returns {HTMLElement[]} Array of options.
   */
  getItems() {
    return this._items;
  }

  /**
   * Check whether given item is selectable.
   *
   * @param {HTMLElement} item Item to check.
   * @param {boolean} [allowExcluded=false] Whether excluded items can be considered selectable.
   * @returns {boolean} True if item can be selected.
   */
  isItemSelectable(item, allowExcluded = false) {
    // Item is hidden or disabled
    if (item.hidden || item.getAttribute('aria-disabled') === 'true') {
      return false;
    }

    // Item belongs to disabled or hidden group
    if (item.closest('[role="group"][aria-disabled="true"], [role="group"][hidden]')) {
      return false;
    }

    // Item is excluded by filter
    const filtered = this.getFilteredItems();
    if (!allowExcluded && filtered && !filtered.includes(item.dataset.value)) {
      return false;
    }

    return true;
  }

  /**
   * Get options that may be selected.
   *
   * @returns {HTMLElement[]} Array of custom options that can be selected.
   */
  getSelectableItems() {
    return this.getItems().filter(option => this.isItemSelectable(option));
  }

  /**
   * Finds next selectable item that is specified position amount from reference item.
   *
   * @param {HTMLElement} item Reference item which is used as starting point. Defaults to first selected item or first item if none was selected.
   * @param {number} position Position change compared to reference item. Can be negative number.
   * @param {boolean} wrap If true, overflowing position is allowed to wrap.
   * @returns {HTMLElement|null} Found item or null. Item may equal to reference item if position did not change.
   */
  findSelectableItem(item = null, position = 0, wrap = false) {
    if (!item) {
      const items = this.getSelectableItems();
      const activeItem = items.find(option => option.getAttribute('aria-selected') === 'true') || (items.length > 0 ? items[0] : null);
      if (!activeItem) {
        return null;
      }
      item = activeItem;
    }

    const options = this.getItems().filter(option => this.isItemSelectable(option) || option === item);
    let selectedIndex = options.findIndex(option => option === item);

    if (selectedIndex === -1) {
      return null;
    }

    let nextIndex = selectedIndex + position;

    // Wrap overflow
    if (wrap) {
      if (nextIndex >= options.length) {
        nextIndex = nextIndex - (options.length + 1);
      }
      else if (nextIndex < 0) {
        nextIndex = options.length + nextIndex;
      }
    }

    // Ensure that index stays in boundaries
    if (nextIndex >= options.length) {
      nextIndex = options.length - 1;
    }
    else if (nextIndex < 0) {
      nextIndex = 0;
    }

    // Position did not change and item is not selectable
    if (nextIndex === selectedIndex && !this.isItemSelectable(item)) {
      return null;
    }

    return options[nextIndex];
  }

  /**
   * Get selected items.
   *
   * @returns {HTMLElement[]}
   */
  getSelectedItems() {
    return this.getItems().filter(item => item.getAttribute('aria-selected') === 'true');
  }

  /**
   * Ensures that provided item is visible in listbox.
   *
   * @param {HTMLElement} item Item which is scrolled in view.
   * @param {string} placement Defines placement of item in listbox: top, center, bottom.
   */
  scrollToItem(item, placement) {
    const container = this.getElements().list;
    const containerRect = container.getBoundingClientRect();
    const rect = item.getBoundingClientRect();

    if (item.offsetTop < container.scrollTop || item.offsetTop + rect.height > container.scrollTop + containerRect.height) {
      let y = item.offsetTop;
      if (placement === 'bottom') {
        y = item.offsetTop + rect.height - containerRect.height;
      }
      else if (placement === 'center') {
        y = item.offsetTop - (containerRect.height / 2) + (rect.height / 2);
      }
      container.scrollTop = y;
    }
  }

  /**
   * Get option item that is currently active.
   *
   * @returns {HTMLElement}
   */
  getActiveItem() {
    return this.getItems().find(item => item.dataset.active === 'true');
  }

  /**
   * Set item which is currently active in the custom listbox element.
   *
   * @param {HTMLElement|null} item Item which was set as active. If empty, currently active item is inactivated.
   * @param {boolean} [scrollToItem=true] Whether item is scrolled in view.
   * @param {string} [scrollPlacement='center'] Placement of item in listbox view after scroll: top, center, bottom.
   */
  setActiveItem(item, scrollToItem = true, scrollPlacement = 'center') {
    if (item && this.getActiveItem() === item) {
      return;
    }

    const config = this.getConfig();
    const options = this.getItems();
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (item && option.dataset.value === item.dataset.value) {
        option.classList.add(config.cssOptionActiveClass);
        option.dataset.active = 'true';
      }
      else {
        option.classList.remove(config.cssOptionActiveClass);
        option.dataset.active = 'false';
      }
    }

    if (item) {
      this.getElements().control.setAttribute('aria-activedescendant', item.id);
      if (scrollToItem) {
        this.scrollToItem(item, scrollPlacement);
      }
    }
    else {
      this.getElements().control.removeAttribute('aria-activedescendant');
    }

    this.getConfig()?.onItemActivated?.call(this, item);
    this.dispatchEvent('active', item);
  }

  /**
   * Checks whether combobox is multiselect.
   *
   * @returns {boolean} True if multiselect is enabled.
   */
  isMultiSelect() {
    return this.getElements().list.getAttribute('aria-multiselectable') === 'true';
  }

  /**
   * Get current select value.
   *
   * @returns {string[]} Array of selected values.
   */
  getValue() {
    return this.getSelectedItems().map(item => item.dataset.value);
  }

  /**
   * Updates values without triggering event.
   *
   * @param {string[]} value New value.
   * @return {HTMLElement[]} Selected items that are in dom.
   * @protected
   */
  _updateValue(value) {
    value = this.isMultiSelect() ? value : value.slice(0, 1);

    const selectedItems = [];

    // Update dom elements
    this.getItems().forEach(option => {
      const selected = value.includes(option.dataset.value);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      option.classList.toggle(this.getConfig().cssOptionSelectedClass, selected);
      if (selected) {
        selectedItems.push(option);
      }
    });

    this.renderSelection();

    return selectedItems;
  }

  /**
   * Sets value and triggers event.
   *
   * @param {string[]} value New value.
   */
  setValue(value) {
    if (this.getElements().list.getAttribute('data-disabled') === 'true') {
      return;
    }
    const oldValue = this.getValue();
    const selectedItems = this._updateValue(value);

    this.getConfig()?.onItemSelected?.call(this, selectedItems, value, oldValue);
    this.dispatchEvent('value', { value, oldValue, selectedItems });

    this.getDataSource().setValue(value);
  }

  /**
   * Adds/removes provided value from current selection.
   *
   * @param {string} interactedValue Value to toggle.
   */
  toggleValue(interactedValue) {
    if (!this.isMultiSelect()) {
      this.setValue([interactedValue]);
    }
    else {
      const values = this.getValue();
      const index = values.indexOf(interactedValue);
      if (index > -1) {
        values.splice(index, 1);
      }
      else {
        values.push(interactedValue);
      }
      this.setValue(values);
    }
  }

  /**
   * Get filtered items.
   *
   * @returns {string[]|null} Array of values that are included or null if no filtering is applied.
   */
  getFilteredItems() {
    return this._filteredItems;
  }

  /**
   * Set filtered items.
   *
   * @param {string[]|null} items Array of item values.
   */
  setFilteredItems(items) {
    this._filteredItems = items;
    this.renderContent();
  }

  /**
   * Check whether combobox list is currently open.
   *
   * @returns {Boolean}
   */
  isOpen() {
    return this.getElements().container.classList.contains(this.getConfig().cssContainerOpenedClass);
  }

  /**
   * Open combobox list.
   *
   * @param {boolean} [focusOnOpen=false] Whether control element should be focused.
   */
  open(focusOnOpen = false) {
    if (this.isOpen()) {
      return;
    }

    const elements = this.getElements();
    elements.control.setAttribute('aria-expanded', 'true');
    elements.container.classList.add(this.getConfig().cssContainerOpenedClass);
    elements.list.scrollTop = 0;

    // Set first selection active
    this.setActiveItem(this.findSelectableItem(null, 0));

    // Move focus to button element
    if (focusOnOpen && document.activeElement !== elements.control) {
      elements.control.focus();
    }

    this.getConfig()?.onToggle?.call(this, true);
    this.dispatchEvent('toggle', true);
  }

  /**
   * Close combobox list.
   *
   * @param {boolean} [focusOnClose=false] Whether control element should be focused.
   */
  close(focusOnClose = false) {
    if (!this.isOpen()) {
      return;
    }

    const elements = this.getElements();
    elements.control.setAttribute('aria-expanded', 'false');
    elements.container.classList.remove(this.getConfig().cssContainerOpenedClass);
    this.setActiveItem(null);

    // Move focus to button element
    if (focusOnClose && document.activeElement !== elements.control) {
      elements.control.focus();
    }

    this.getConfig()?.onToggle?.call(this, false);
    this.dispatchEvent('toggle', false);
  }

  /**
   * Destroys combobox instance.
   */
  destroy() {
    this.getConfig()?.onDestroy?.call(this);
    this.dispatchEvent('destroy');

    document.removeEventListener('click', this._onDocumentClick);

    const elements = this.getElements();
    const select = this.getDataSource()?.getNativeSelect();

    // Restore label
    if (elements.label) {
      elements.label.removeEventListener('click', this._onLabelClick);
      elements.label.htmlFor = select?.id || '';
    }

    // Show native select and remove reference
    if (select) {
      select.style.display = '';
      select.removeAttribute('data-combobox');
    }

    // Remove custom select input
    elements.container.remove();
    this._elements = null;
  }
}

/**
 * @export
 * @typedef {object} ComboboxElements
 * @property {HTMLElement} container Container element for combobox.
 * @property {HTMLLabelElement} label Label element that labels combobox.
 * @property {HTMLButtonElement|HTMLInputElement} control Control element that controls combobox list.
 * @property {HTMLElement} list Combobox list element.
 * @property {HTMLElement[]} options Combobox list items.
 */


/**
 * @export
 * @typedef {object} ComboboxOptions
 * @property {HTMLSelectElement} [select] Reads default values from select and creates new SelectDataSource that controls combobox content.
 * @property {HTMLElement} [target] Required if select is not defined. Given element is replaced with generated combobox.
 * @property {SelectDataSource} [dataSource] Data source object. Created automatically if select is given.
 * @property {HTMLLabelElement} [label] Label element that labels combobox. If select was given, reassigns select label to combobox.
 * @property {string} [id] Custom id for combobox, used as prefix for child elements.
 * @property {string} [cssClassPrefix] Class for custom select input wrapper.
 * @property {string} [cssContainerClass] Class for custom select input wrapper.
 * @property {string} [cssContainerOpenedClass] Class for container when listbox is open.
 * @property {string} [cssControlClass] Class for control element.
 * @property {string} [cssListClass] Class for listbox is open.
 * @property {string} [cssOptionClass] Class for list option.
 * @property {string} [cssOptionActiveClass] Class for active option.
 * @property {string} [cssOptionSelectedClass] Class for selected options.
 * @property {string} [cssOptionDisabledClass] Class for disabled options.
 * @property {string} [cssOptgroupClass] Class for option group.
 * @property {string} [cssOptgroupDisabledClass] Class for disabled option group.
 * @property {string} [cssOptgroupLabelClass] Class for option group label.
 * @property {string} [cssNoResultsClass] Class for no results element.
 * @property {string} [noResults] HTML content that is shown in listbox if filter callback did not match any options.
 * @property {number|null} [maxSelections=null] Maximum amount of selections.
 * @property {number} [minSelections=0] Minimum amount of selections.
 * @property {boolean} [allowEmpty=true] Whether option with empty value is shown in option list. If false and placeholder is not set, removed empty option is used as placeholder.
 * @property {string} [placeholder] Placeholder text that is shown when input has no selections.
 * @property {function():void} [onDestroy] Called when element is being destroyed, element with all references will still exist at this point.
 * @property {function():void} [onInit] Called after element has been rendered.
 * @property {function(HTMLElement|null):void} [onItemActivated] Called when item was (de)activated.
 * @property {function(HTMLElement):void} [onItemRender] Callback that receives option (or option group) element that is being rendered.
 * @property {function():void} [onItemSelected] Called when new value was set.
 * @property {function():void} [onRender] Called when rendering is completed.
 * @property {function(boolean):void} [onToggle] Called when combobox is opened or closed.
 * @property {function(object[]):void} [onUpdate] Called when combobox datasource was updated.
 * @property {function(HTMLElement[], string[], string[]):string|null} [selectionRenderer] Callback that receives selected items, new value and old. If string is returned it is used instead of calculated value.
 * @property {string} [browserAutocomplete] Value for browser autocomplete.
 */

export default Combobox;
